export interface Calculator {
  id: string;
  name: string;
  shortName: string;
  description: string;
  category: 'critical-care' | 'cardiology' | 'nephrology' | 'infectious' | 'pediatrics' | 'general';
  inputs: CalculatorInput[];
  calculate: (values: Record<string, number | string>) => CalculatorResult;
}

export interface CalculatorInput {
  id: string;
  label: string;
  type: 'number' | 'select' | 'boolean';
  options?: { value: string | number; label: string }[];
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
}

export interface CalculatorResult {
  score: number | string;
  interpretation: string;
  riskLevel: 'low' | 'moderate' | 'high' | 'critical';
  details: Record<string, string | number>;
}

// 1. NEWS2 - National Early Warning Score 2
export const news2Calculator: Calculator = {
  id: 'news2',
  name: 'National Early Warning Score 2 (NEWS2)',
  shortName: 'NEWS2',
  description: 'Early warning score for detecting clinical deterioration in acute illness',
  category: 'critical-care',
  inputs: [
    {
      id: 'respiratoryRate',
      label: 'Respiratory Rate',
      type: 'number',
      unit: 'breaths/min',
      min: 0,
      max: 60,
      placeholder: '12-20 normal'
    },
    {
      id: 'spo2',
      label: 'SpO2',
      type: 'number',
      unit: '%',
      min: 0,
      max: 100,
      placeholder: '94-100 normal'
    },
    {
      id: 'onOxygen',
      label: 'On Supplemental Oxygen?',
      type: 'select',
      options: [
        { value: 'no', label: 'No (Room Air)' },
        { value: 'yes', label: 'Yes' }
      ]
    },
    {
      id: 'temperature',
      label: 'Temperature',
      type: 'number',
      unit: '°C',
      min: 30,
      max: 45,
      step: 0.1,
      placeholder: '36.1-38.0 normal'
    },
    {
      id: 'systolicBP',
      label: 'Systolic Blood Pressure',
      type: 'number',
      unit: 'mmHg',
      min: 0,
      max: 300,
      placeholder: '111-219 normal'
    },
    {
      id: 'heartRate',
      label: 'Heart Rate',
      type: 'number',
      unit: 'bpm',
      min: 0,
      max: 250,
      placeholder: '51-90 normal'
    },
    {
      id: 'consciousness',
      label: 'Level of Consciousness',
      type: 'select',
      options: [
        { value: 'alert', label: 'Alert' },
        { value: 'confusion', label: 'New Confusion (CVPU)' },
        { value: 'voice', label: 'Responds to Voice' },
        { value: 'pain', label: 'Responds to Pain' },
        { value: 'unresponsive', label: 'Unresponsive' }
      ]
    }
  ],
  calculate: (values) => {
    let score = 0;
    const details: Record<string, number> = {};

    // Respiratory Rate scoring
    const rr = Number(values.respiratoryRate);
    if (rr <= 8) { details.respiratoryRate = 3; score += 3; }
    else if (rr <= 11) { details.respiratoryRate = 1; score += 1; }
    else if (rr <= 20) { details.respiratoryRate = 0; }
    else if (rr <= 24) { details.respiratoryRate = 2; score += 2; }
    else { details.respiratoryRate = 3; score += 3; }

    // SpO2 scoring
    const spo2 = Number(values.spo2);
    if (values.onOxygen === 'no') {
      if (spo2 <= 91) { details.spo2 = 3; score += 3; }
      else if (spo2 <= 93) { details.spo2 = 2; score += 2; }
      else if (spo2 <= 95) { details.spo2 = 1; score += 1; }
      else { details.spo2 = 0; }
    } else {
      if (spo2 <= 83) { details.spo2 = 3; score += 3; }
      else if (spo2 <= 85) { details.spo2 = 2; score += 2; }
      else if (spo2 <= 87) { details.spo2 = 1; score += 1; }
      else if (spo2 <= 92) { details.spo2 = 0; }
      else { details.spo2 = 2; score += 2; } // SpO2 >92% on O2
    }

    // Oxygen supplementation
    if (values.onOxygen === 'yes') { details.oxygen = 2; score += 2; }
    else { details.oxygen = 0; }

    // Temperature scoring
    const temp = Number(values.temperature);
    if (temp <= 35.0) { details.temperature = 3; score += 3; }
    else if (temp <= 36.0) { details.temperature = 1; score += 1; }
    else if (temp <= 38.0) { details.temperature = 0; }
    else if (temp <= 39.0) { details.temperature = 1; score += 1; }
    else { details.temperature = 2; score += 2; }

    // Systolic BP scoring
    const sbp = Number(values.systolicBP);
    if (sbp <= 90) { details.systolicBP = 3; score += 3; }
    else if (sbp <= 100) { details.systolicBP = 2; score += 2; }
    else if (sbp <= 110) { details.systolicBP = 1; score += 1; }
    else if (sbp <= 219) { details.systolicBP = 0; }
    else { details.systolicBP = 3; score += 3; }

    // Heart Rate scoring
    const hr = Number(values.heartRate);
    if (hr <= 40) { details.heartRate = 3; score += 3; }
    else if (hr <= 50) { details.heartRate = 1; score += 1; }
    else if (hr <= 90) { details.heartRate = 0; }
    else if (hr <= 110) { details.heartRate = 1; score += 1; }
    else if (hr <= 130) { details.heartRate = 2; score += 2; }
    else { details.heartRate = 3; score += 3; }

    // Consciousness scoring
    if (values.consciousness === 'alert') { details.consciousness = 0; }
    else { details.consciousness = 3; score += 3; }

    let interpretation = '';
    let riskLevel: 'low' | 'moderate' | 'high' | 'critical' = 'low';

    if (score <= 4) {
      interpretation = 'Low risk - Ward-based care appropriate';
      riskLevel = 'low';
    } else if (score <= 6) {
      interpretation = 'Medium risk - Urgent review needed';
      riskLevel = 'moderate';
    } else {
      interpretation = 'High risk - Emergency response required';
      riskLevel = score >= 7 ? 'critical' : 'high';
    }

    return { score, interpretation, riskLevel, details };
  }
};

// 2. CURB-65 - Pneumonia Severity
export const curb65Calculator: Calculator = {
  id: 'curb65',
  name: 'CURB-65 Pneumonia Severity Score',
  shortName: 'CURB-65',
  description: 'Assesses severity of community-acquired pneumonia to guide treatment setting',
  category: 'infectious',
  inputs: [
    {
      id: 'confusion',
      label: 'New Confusion (AMT ≤8 or new disorientation)',
      type: 'select',
      options: [
        { value: 0, label: 'No' },
        { value: 1, label: 'Yes' }
      ]
    },
    {
      id: 'urea',
      label: 'Blood Urea Nitrogen',
      type: 'number',
      unit: 'mmol/L',
      min: 0,
      max: 100,
      placeholder: '>7 mmol/L = 1 point'
    },
    {
      id: 'respiratoryRate',
      label: 'Respiratory Rate',
      type: 'number',
      unit: 'breaths/min',
      min: 0,
      max: 60,
      placeholder: '≥30 = 1 point'
    },
    {
      id: 'systolicBP',
      label: 'Systolic Blood Pressure',
      type: 'number',
      unit: 'mmHg',
      min: 0,
      max: 300,
      placeholder: '<90 = 1 point'
    },
    {
      id: 'diastolicBP',
      label: 'Diastolic Blood Pressure',
      type: 'number',
      unit: 'mmHg',
      min: 0,
      max: 200,
      placeholder: '≤60 = 1 point'
    },
    {
      id: 'age',
      label: 'Age',
      type: 'number',
      unit: 'years',
      min: 0,
      max: 120,
      placeholder: '≥65 = 1 point'
    }
  ],
  calculate: (values) => {
    let score = 0;
    const details: Record<string, number> = {};

    // Confusion
    details.confusion = Number(values.confusion);
    score += details.confusion;

    // Urea > 7 mmol/L
    if (Number(values.urea) > 7) { details.urea = 1; score += 1; }
    else { details.urea = 0; }

    // Respiratory rate ≥ 30
    if (Number(values.respiratoryRate) >= 30) { details.respiratoryRate = 1; score += 1; }
    else { details.respiratoryRate = 0; }

    // Blood pressure: SBP < 90 or DBP ≤ 60
    if (Number(values.systolicBP) < 90 || Number(values.diastolicBP) <= 60) {
      details.bloodPressure = 1; score += 1;
    } else { details.bloodPressure = 0; }

    // Age ≥ 65
    if (Number(values.age) >= 65) { details.age = 1; score += 1; }
    else { details.age = 0; }

    let interpretation = '';
    let riskLevel: 'low' | 'moderate' | 'high' | 'critical' = 'low';

    if (score === 0 || score === 1) {
      interpretation = 'Low severity - Consider outpatient treatment';
      riskLevel = 'low';
    } else if (score === 2) {
      interpretation = 'Moderate severity - Consider hospital admission';
      riskLevel = 'moderate';
    } else if (score === 3) {
      interpretation = 'Severe pneumonia - Hospital admission required';
      riskLevel = 'high';
    } else {
      interpretation = 'Very severe - Consider ICU admission';
      riskLevel = 'critical';
    }

    return { score, interpretation, riskLevel, details };
  }
};

// 3. Glasgow Coma Scale
export const gcsCalculator: Calculator = {
  id: 'gcs',
  name: 'Glasgow Coma Scale (GCS)',
  shortName: 'GCS',
  description: 'Assesses level of consciousness after brain injury',
  category: 'critical-care',
  inputs: [
    {
      id: 'eyeResponse',
      label: 'Eye Opening',
      type: 'select',
      options: [
        { value: 4, label: '4 - Spontaneous' },
        { value: 3, label: '3 - To verbal command' },
        { value: 2, label: '2 - To pain' },
        { value: 1, label: '1 - No response' }
      ]
    },
    {
      id: 'verbalResponse',
      label: 'Verbal Response',
      type: 'select',
      options: [
        { value: 5, label: '5 - Oriented' },
        { value: 4, label: '4 - Confused' },
        { value: 3, label: '3 - Inappropriate words' },
        { value: 2, label: '2 - Incomprehensible sounds' },
        { value: 1, label: '1 - No response' }
      ]
    },
    {
      id: 'motorResponse',
      label: 'Motor Response',
      type: 'select',
      options: [
        { value: 6, label: '6 - Obeys commands' },
        { value: 5, label: '5 - Localizes pain' },
        { value: 4, label: '4 - Withdraws from pain' },
        { value: 3, label: '3 - Abnormal flexion (decorticate)' },
        { value: 2, label: '2 - Extension (decerebrate)' },
        { value: 1, label: '1 - No response' }
      ]
    }
  ],
  calculate: (values) => {
    const eye = Number(values.eyeResponse);
    const verbal = Number(values.verbalResponse);
    const motor = Number(values.motorResponse);
    const score = eye + verbal + motor;

    const details = {
      eyeResponse: eye,
      verbalResponse: verbal,
      motorResponse: motor
    };

    let interpretation = '';
    let riskLevel: 'low' | 'moderate' | 'high' | 'critical' = 'low';

    if (score >= 13) {
      interpretation = 'Mild brain injury - Good prognosis';
      riskLevel = 'low';
    } else if (score >= 9) {
      interpretation = 'Moderate brain injury - Close monitoring required';
      riskLevel = 'moderate';
    } else if (score >= 6) {
      interpretation = 'Severe brain injury - ICU care recommended';
      riskLevel = 'high';
    } else {
      interpretation = 'Very severe brain injury - Critical condition';
      riskLevel = 'critical';
    }

    return { score, interpretation, riskLevel, details };
  }
};

// 4. BMI Calculator
export const bmiCalculator: Calculator = {
  id: 'bmi',
  name: 'Body Mass Index (BMI)',
  shortName: 'BMI',
  description: 'Calculates body mass index from height and weight',
  category: 'general',
  inputs: [
    {
      id: 'weight',
      label: 'Weight',
      type: 'number',
      unit: 'kg',
      min: 1,
      max: 500,
      step: 0.1,
      placeholder: 'Enter weight in kg'
    },
    {
      id: 'height',
      label: 'Height',
      type: 'number',
      unit: 'cm',
      min: 50,
      max: 300,
      placeholder: 'Enter height in cm'
    }
  ],
  calculate: (values) => {
    const weight = Number(values.weight);
    const heightM = Number(values.height) / 100;
    const bmi = weight / (heightM * heightM);
    const score = Math.round(bmi * 10) / 10;

    const details = {
      weight,
      heightCm: Number(values.height),
      heightM: Math.round(heightM * 100) / 100
    };

    let interpretation = '';
    let riskLevel: 'low' | 'moderate' | 'high' | 'critical' = 'low';

    if (bmi < 16) {
      interpretation = 'Severely underweight - Nutritional intervention urgent';
      riskLevel = 'critical';
    } else if (bmi < 18.5) {
      interpretation = 'Underweight - Nutritional assessment recommended';
      riskLevel = 'moderate';
    } else if (bmi < 25) {
      interpretation = 'Normal weight - Healthy BMI range';
      riskLevel = 'low';
    } else if (bmi < 30) {
      interpretation = 'Overweight - Lifestyle modification advised';
      riskLevel = 'moderate';
    } else if (bmi < 35) {
      interpretation = 'Obese Class I - Medical intervention recommended';
      riskLevel = 'high';
    } else if (bmi < 40) {
      interpretation = 'Obese Class II - Significant health risks';
      riskLevel = 'high';
    } else {
      interpretation = 'Obese Class III - Severe health risks, specialist referral';
      riskLevel = 'critical';
    }

    return { score, interpretation, riskLevel, details };
  }
};

// 5. eGFR Calculator (CKD-EPI)
export const egfrCalculator: Calculator = {
  id: 'egfr',
  name: 'eGFR (CKD-EPI Equation)',
  shortName: 'eGFR',
  description: 'Estimates glomerular filtration rate for kidney function assessment',
  category: 'nephrology',
  inputs: [
    {
      id: 'creatinine',
      label: 'Serum Creatinine',
      type: 'number',
      unit: 'µmol/L',
      min: 10,
      max: 2000,
      placeholder: 'Enter creatinine'
    },
    {
      id: 'age',
      label: 'Age',
      type: 'number',
      unit: 'years',
      min: 18,
      max: 120,
      placeholder: 'Enter age'
    },
    {
      id: 'sex',
      label: 'Sex',
      type: 'select',
      options: [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' }
      ]
    }
  ],
  calculate: (values) => {
    const creatinine = Number(values.creatinine) / 88.4; // Convert µmol/L to mg/dL
    const age = Number(values.age);
    const isFemale = values.sex === 'female';

    let kappa = isFemale ? 0.7 : 0.9;
    let alpha = isFemale ? -0.329 : -0.411;
    let sexMultiplier = isFemale ? 1.018 : 1;

    const creatRatio = creatinine / kappa;
    const minRatio = Math.min(creatRatio, 1);
    const maxRatio = Math.max(creatRatio, 1);

    const egfr = 141 * Math.pow(minRatio, alpha) * Math.pow(maxRatio, -1.209) * Math.pow(0.993, age) * sexMultiplier;
    const score = Math.round(egfr);

    const details = {
      creatinineMgDl: Math.round(creatinine * 100) / 100,
      age,
      sex: values.sex as string
    };

    let interpretation = '';
    let riskLevel: 'low' | 'moderate' | 'high' | 'critical' = 'low';

    if (egfr >= 90) {
      interpretation = 'G1: Normal or high kidney function';
      riskLevel = 'low';
    } else if (egfr >= 60) {
      interpretation = 'G2: Mildly decreased kidney function';
      riskLevel = 'low';
    } else if (egfr >= 45) {
      interpretation = 'G3a: Mild to moderately decreased function';
      riskLevel = 'moderate';
    } else if (egfr >= 30) {
      interpretation = 'G3b: Moderate to severely decreased function';
      riskLevel = 'moderate';
    } else if (egfr >= 15) {
      interpretation = 'G4: Severely decreased kidney function';
      riskLevel = 'high';
    } else {
      interpretation = 'G5: Kidney failure - Dialysis may be needed';
      riskLevel = 'critical';
    }

    return { score: `${score} mL/min/1.73m²`, interpretation, riskLevel, details };
  }
};

// 6. CHA₂DS₂-VASc Score
export const cha2ds2vascCalculator: Calculator = {
  id: 'cha2ds2vasc',
  name: 'CHA₂DS₂-VASc Score',
  shortName: 'CHA₂DS₂-VASc',
  description: 'Stroke risk assessment in patients with atrial fibrillation',
  category: 'cardiology',
  inputs: [
    {
      id: 'chf',
      label: 'Congestive Heart Failure',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes (+1)' }]
    },
    {
      id: 'hypertension',
      label: 'Hypertension',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes (+1)' }]
    },
    {
      id: 'age75',
      label: 'Age ≥75 years',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 2, label: 'Yes (+2)' }]
    },
    {
      id: 'diabetes',
      label: 'Diabetes Mellitus',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes (+1)' }]
    },
    {
      id: 'stroke',
      label: 'Prior Stroke/TIA/Thromboembolism',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 2, label: 'Yes (+2)' }]
    },
    {
      id: 'vascular',
      label: 'Vascular Disease (MI, PAD, Aortic plaque)',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes (+1)' }]
    },
    {
      id: 'age65',
      label: 'Age 65-74 years',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes (+1)' }]
    },
    {
      id: 'sex',
      label: 'Sex Category',
      type: 'select',
      options: [{ value: 0, label: 'Male' }, { value: 1, label: 'Female (+1)' }]
    }
  ],
  calculate: (values) => {
    const score = Number(values.chf) + Number(values.hypertension) + Number(values.age75) +
                  Number(values.diabetes) + Number(values.stroke) + Number(values.vascular) +
                  Number(values.age65) + Number(values.sex);

    const details = {
      chf: Number(values.chf),
      hypertension: Number(values.hypertension),
      age75: Number(values.age75),
      diabetes: Number(values.diabetes),
      stroke: Number(values.stroke),
      vascular: Number(values.vascular),
      age65: Number(values.age65),
      sex: Number(values.sex)
    };

    // Annual stroke risk percentages
    const strokeRisk: Record<number, string> = {
      0: '0%', 1: '1.3%', 2: '2.2%', 3: '3.2%', 4: '4.0%',
      5: '6.7%', 6: '9.8%', 7: '9.6%', 8: '6.7%', 9: '15.2%'
    };

    let interpretation = '';
    let riskLevel: 'low' | 'moderate' | 'high' | 'critical' = 'low';

    if (score === 0) {
      interpretation = `Low risk (${strokeRisk[0]} annual stroke risk) - No anticoagulation needed`;
      riskLevel = 'low';
    } else if (score === 1) {
      interpretation = `Low-moderate risk (${strokeRisk[1]} annual stroke risk) - Consider anticoagulation`;
      riskLevel = 'low';
    } else if (score <= 3) {
      interpretation = `Moderate risk (${strokeRisk[score]} annual stroke risk) - Anticoagulation recommended`;
      riskLevel = 'moderate';
    } else {
      interpretation = `High risk (${strokeRisk[Math.min(score, 9)]} annual stroke risk) - Anticoagulation strongly recommended`;
      riskLevel = score >= 5 ? 'critical' : 'high';
    }

    return { score, interpretation, riskLevel, details };
  }
};

// 7. Wells Score for DVT
export const wellsDvtCalculator: Calculator = {
  id: 'wells-dvt',
  name: 'Wells Score for DVT',
  shortName: 'Wells DVT',
  description: 'Clinical prediction rule for deep vein thrombosis probability',
  category: 'cardiology',
  inputs: [
    {
      id: 'activeCancer',
      label: 'Active cancer (treatment within 6 months)',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes (+1)' }]
    },
    {
      id: 'paralysis',
      label: 'Paralysis, paresis, or recent cast immobilization',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes (+1)' }]
    },
    {
      id: 'bedridden',
      label: 'Bedridden >3 days or major surgery within 12 weeks',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes (+1)' }]
    },
    {
      id: 'tenderness',
      label: 'Localized tenderness along deep venous system',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes (+1)' }]
    },
    {
      id: 'legSwelling',
      label: 'Entire leg swollen',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes (+1)' }]
    },
    {
      id: 'calfSwelling',
      label: 'Calf swelling >3cm compared to other leg',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes (+1)' }]
    },
    {
      id: 'pittingEdema',
      label: 'Pitting edema in symptomatic leg only',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes (+1)' }]
    },
    {
      id: 'collateralVeins',
      label: 'Collateral superficial veins (non-varicose)',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes (+1)' }]
    },
    {
      id: 'previousDvt',
      label: 'Previously documented DVT',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes (+1)' }]
    },
    {
      id: 'alternativeDiagnosis',
      label: 'Alternative diagnosis at least as likely',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: -2, label: 'Yes (-2)' }]
    }
  ],
  calculate: (values) => {
    const score = Number(values.activeCancer) + Number(values.paralysis) + Number(values.bedridden) +
                  Number(values.tenderness) + Number(values.legSwelling) + Number(values.calfSwelling) +
                  Number(values.pittingEdema) + Number(values.collateralVeins) + Number(values.previousDvt) +
                  Number(values.alternativeDiagnosis);

    const details = { totalScore: score };

    let interpretation = '';
    let riskLevel: 'low' | 'moderate' | 'high' | 'critical' = 'low';

    if (score <= 0) {
      interpretation = 'Low probability (5%) - D-dimer testing, if negative DVT excluded';
      riskLevel = 'low';
    } else if (score <= 2) {
      interpretation = 'Moderate probability (17%) - D-dimer or ultrasound recommended';
      riskLevel = 'moderate';
    } else if (score <= 5) {
      interpretation = 'High probability (53%) - Ultrasound recommended, consider empiric anticoagulation';
      riskLevel = 'high';
    } else {
      interpretation = 'Very high probability (>75%) - Immediate anticoagulation, urgent ultrasound, monitor for PE';
      riskLevel = 'critical';
    }

    return { score, interpretation, riskLevel, details };
  }
};

// 8. APGAR Score
export const apgarCalculator: Calculator = {
  id: 'apgar',
  name: 'APGAR Score',
  shortName: 'APGAR',
  description: 'Assessment of newborn health at birth',
  category: 'pediatrics',
  inputs: [
    {
      id: 'appearance',
      label: 'Appearance (Skin Color)',
      type: 'select',
      options: [
        { value: 0, label: '0 - Blue/pale all over' },
        { value: 1, label: '1 - Blue extremities, pink body' },
        { value: 2, label: '2 - Completely pink' }
      ]
    },
    {
      id: 'pulse',
      label: 'Pulse (Heart Rate)',
      type: 'select',
      options: [
        { value: 0, label: '0 - Absent' },
        { value: 1, label: '1 - <100 bpm' },
        { value: 2, label: '2 - ≥100 bpm' }
      ]
    },
    {
      id: 'grimace',
      label: 'Grimace (Reflex Irritability)',
      type: 'select',
      options: [
        { value: 0, label: '0 - No response' },
        { value: 1, label: '1 - Grimace/weak cry' },
        { value: 2, label: '2 - Cry, cough, sneeze' }
      ]
    },
    {
      id: 'activity',
      label: 'Activity (Muscle Tone)',
      type: 'select',
      options: [
        { value: 0, label: '0 - Limp/flaccid' },
        { value: 1, label: '1 - Some flexion' },
        { value: 2, label: '2 - Active movement' }
      ]
    },
    {
      id: 'respiration',
      label: 'Respiration',
      type: 'select',
      options: [
        { value: 0, label: '0 - Absent' },
        { value: 1, label: '1 - Slow/irregular' },
        { value: 2, label: '2 - Good, crying' }
      ]
    },
    {
      id: 'timing',
      label: 'Assessment Timing',
      type: 'select',
      options: [
        { value: '1min', label: '1 minute after birth' },
        { value: '5min', label: '5 minutes after birth' },
        { value: '10min', label: '10 minutes after birth' }
      ]
    }
  ],
  calculate: (values) => {
    const score = Number(values.appearance) + Number(values.pulse) + Number(values.grimace) +
                  Number(values.activity) + Number(values.respiration);

    const details = {
      appearance: Number(values.appearance),
      pulse: Number(values.pulse),
      grimace: Number(values.grimace),
      activity: Number(values.activity),
      respiration: Number(values.respiration),
      timing: values.timing as string
    };

    let interpretation = '';
    let riskLevel: 'low' | 'moderate' | 'high' | 'critical' = 'low';

    if (score >= 7) {
      interpretation = 'Normal - Baby in good condition';
      riskLevel = 'low';
    } else if (score >= 4) {
      interpretation = 'Moderately abnormal - Some resuscitation may be needed';
      riskLevel = 'moderate';
    } else {
      interpretation = 'Low score - Immediate resuscitation required';
      riskLevel = 'critical';
    }

    return { score, interpretation, riskLevel, details };
  }
};

// 9. MEWS - Modified Early Warning Score
export const mewsCalculator: Calculator = {
  id: 'mews',
  name: 'Modified Early Warning Score (MEWS)',
  shortName: 'MEWS',
  description: 'Simplified early warning score for clinical deterioration',
  category: 'critical-care',
  inputs: [
    {
      id: 'systolicBP',
      label: 'Systolic Blood Pressure',
      type: 'number',
      unit: 'mmHg',
      min: 0,
      max: 300,
      placeholder: '101-199 normal'
    },
    {
      id: 'heartRate',
      label: 'Heart Rate',
      type: 'number',
      unit: 'bpm',
      min: 0,
      max: 250,
      placeholder: '51-100 normal'
    },
    {
      id: 'respiratoryRate',
      label: 'Respiratory Rate',
      type: 'number',
      unit: 'breaths/min',
      min: 0,
      max: 60,
      placeholder: '9-14 normal'
    },
    {
      id: 'temperature',
      label: 'Temperature',
      type: 'number',
      unit: '°C',
      min: 30,
      max: 45,
      step: 0.1,
      placeholder: '35-38.4 normal'
    },
    {
      id: 'avpu',
      label: 'AVPU Score',
      type: 'select',
      options: [
        { value: 0, label: 'Alert' },
        { value: 1, label: 'Responds to Voice' },
        { value: 2, label: 'Responds to Pain' },
        { value: 3, label: 'Unresponsive' }
      ]
    }
  ],
  calculate: (values) => {
    let score = 0;
    const details: Record<string, number> = {};

    // Systolic BP
    const sbp = Number(values.systolicBP);
    if (sbp <= 70) { details.systolicBP = 3; score += 3; }
    else if (sbp <= 80) { details.systolicBP = 2; score += 2; }
    else if (sbp <= 100) { details.systolicBP = 1; score += 1; }
    else if (sbp <= 199) { details.systolicBP = 0; }
    else { details.systolicBP = 2; score += 2; }

    // Heart Rate
    const hr = Number(values.heartRate);
    if (hr <= 40) { details.heartRate = 2; score += 2; }
    else if (hr <= 50) { details.heartRate = 1; score += 1; }
    else if (hr <= 100) { details.heartRate = 0; }
    else if (hr <= 110) { details.heartRate = 1; score += 1; }
    else if (hr <= 129) { details.heartRate = 2; score += 2; }
    else { details.heartRate = 3; score += 3; }

    // Respiratory Rate
    const rr = Number(values.respiratoryRate);
    if (rr < 9) { details.respiratoryRate = 2; score += 2; }
    else if (rr <= 14) { details.respiratoryRate = 0; }
    else if (rr <= 20) { details.respiratoryRate = 1; score += 1; }
    else if (rr <= 29) { details.respiratoryRate = 2; score += 2; }
    else { details.respiratoryRate = 3; score += 3; }

    // Temperature
    const temp = Number(values.temperature);
    if (temp < 35) { details.temperature = 2; score += 2; }
    else if (temp <= 38.4) { details.temperature = 0; }
    else { details.temperature = 2; score += 2; }

    // AVPU
    details.avpu = Number(values.avpu);
    score += details.avpu;

    let interpretation = '';
    let riskLevel: 'low' | 'moderate' | 'high' | 'critical' = 'low';

    if (score <= 2) {
      interpretation = 'Low risk - Continue routine monitoring';
      riskLevel = 'low';
    } else if (score <= 4) {
      interpretation = 'Medium risk - Increase monitoring frequency';
      riskLevel = 'moderate';
    } else if (score <= 6) {
      interpretation = 'High risk - Urgent medical review needed';
      riskLevel = 'high';
    } else {
      interpretation = 'Critical - Immediate medical attention required';
      riskLevel = 'critical';
    }

    return { score, interpretation, riskLevel, details };
  }
};

// 10. Malaria Severity Score
export const malariaSeverityCalculator: Calculator = {
  id: 'malaria-severity',
  name: 'Malaria Severity Assessment',
  shortName: 'Malaria Severity',
  description: 'WHO criteria-based assessment for severe malaria',
  category: 'infectious',
  inputs: [
    {
      id: 'consciousness',
      label: 'Impaired Consciousness/Coma',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes' }]
    },
    {
      id: 'prostration',
      label: 'Prostration (unable to sit/stand/walk)',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes' }]
    },
    {
      id: 'convulsions',
      label: 'Multiple Convulsions (>2 in 24 hours)',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes' }]
    },
    {
      id: 'respiratoryDistress',
      label: 'Respiratory Distress (acidotic breathing)',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes' }]
    },
    {
      id: 'pulmonaryEdema',
      label: 'Pulmonary Edema (radiological)',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes' }]
    },
    {
      id: 'shock',
      label: 'Circulatory Collapse/Shock',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes' }]
    },
    {
      id: 'bleeding',
      label: 'Abnormal Bleeding',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes' }]
    },
    {
      id: 'jaundice',
      label: 'Jaundice (with organ dysfunction)',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes' }]
    },
    {
      id: 'hemoglobinuria',
      label: 'Hemoglobinuria (dark/black urine)',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes' }]
    },
    {
      id: 'severeAnemia',
      label: 'Severe Anemia (Hb <5g/dL or Hct <15%)',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes' }]
    },
    {
      id: 'hypoglycemia',
      label: 'Hypoglycemia (<2.2 mmol/L)',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes' }]
    },
    {
      id: 'acidosis',
      label: 'Metabolic Acidosis (bicarbonate <15 mmol/L)',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes' }]
    },
    {
      id: 'renalImpairment',
      label: 'Acute Kidney Injury (creatinine >265 µmol/L)',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes' }]
    },
    {
      id: 'hyperparasitemia',
      label: 'Hyperparasitemia (>10% in non-immune)',
      type: 'select',
      options: [{ value: 0, label: 'No' }, { value: 1, label: 'Yes' }]
    }
  ],
  calculate: (values) => {
    const criteriaCount = Number(values.consciousness) + Number(values.prostration) +
      Number(values.convulsions) + Number(values.respiratoryDistress) +
      Number(values.pulmonaryEdema) + Number(values.shock) + Number(values.bleeding) +
      Number(values.jaundice) + Number(values.hemoglobinuria) + Number(values.severeAnemia) +
      Number(values.hypoglycemia) + Number(values.acidosis) + Number(values.renalImpairment) +
      Number(values.hyperparasitemia);

    const details = {
      totalCriteria: criteriaCount,
      consciousness: Number(values.consciousness),
      prostration: Number(values.prostration),
      convulsions: Number(values.convulsions),
      respiratoryDistress: Number(values.respiratoryDistress),
      shock: Number(values.shock),
      severeAnemia: Number(values.severeAnemia),
      hypoglycemia: Number(values.hypoglycemia),
      renalImpairment: Number(values.renalImpairment)
    };

    let interpretation = '';
    let riskLevel: 'low' | 'moderate' | 'high' | 'critical' = 'low';

    if (criteriaCount === 0) {
      interpretation = 'Uncomplicated malaria - Outpatient treatment with ACT';
      riskLevel = 'low';
    } else if (criteriaCount === 1) {
      interpretation = 'Severe malaria (1 criterion) - Hospital admission, IV artesunate';
      riskLevel = 'high';
    } else if (criteriaCount <= 3) {
      interpretation = 'Severe malaria (multiple criteria) - ICU care, IV artesunate urgent';
      riskLevel = 'critical';
    } else {
      interpretation = 'Very severe/complicated malaria - Intensive care, high mortality risk';
      riskLevel = 'critical';
    }

    return { score: criteriaCount, interpretation, riskLevel, details };
  }
};

// Export all calculators
export const calculators: Calculator[] = [
  news2Calculator,
  curb65Calculator,
  gcsCalculator,
  bmiCalculator,
  egfrCalculator,
  cha2ds2vascCalculator,
  wellsDvtCalculator,
  apgarCalculator,
  mewsCalculator,
  malariaSeverityCalculator
];

export const getCalculatorById = (id: string): Calculator | undefined => {
  return calculators.find(calc => calc.id === id);
};
